import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  Save,
  Eye,
  X,
  Plus,
  Edit2,
  Trash2,
  Search,
  Filter,
  AlertCircle,
  CheckCircle,
  Tag as TagIcon,
  FileText
} from 'lucide-react';
import { knowledgeBaseService } from '../../services/knowledgeBaseService';
import { roleService } from '../../services/roleService';
import TemplatesPanel from './components/TemplatesPanel';

export default function KnowledgeBaseEditor() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showEditorModal, setShowEditorModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [previewMode, setPreviewMode] = useState(false);
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);

  useEffect(() => {
    loadData();
  }, [selectedStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const filters = {};
      if (selectedStatus !== 'all') filters.status = selectedStatus;

      const [articlesData, categoriesData, rolesData] = await Promise.all([
        knowledgeBaseService?.getArticles(filters),
        knowledgeBaseService?.getCategories(),
        roleService?.getAllRoles()
      ]);

      setArticles(articlesData);
      setCategories(categoriesData);
      setRoles(rolesData);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUseTemplate = (templateContent) => {
    setEditingArticle({
      ...editingArticle,
      content: templateContent
    });
    setShowEditorModal(true);
  };

  const handleSaveArticle = async (articleData) => {
    try {
      setError('');
      setSuccess('');

      if (editingArticle?.id) {
        await knowledgeBaseService?.updateArticle(editingArticle?.id, articleData);
        setSuccess('Article updated successfully');
      } else {
        await knowledgeBaseService?.createArticle(articleData);
        setSuccess('Article created successfully');
      }

      setShowEditorModal(false);
      setEditingArticle(null);
      loadData();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!confirm('Are you sure you want to delete this article?')) return;

    try {
      await knowledgeBaseService?.deleteArticle(articleId);
      setSuccess('Article deleted successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message);
    }
  };

  const filteredArticles = articles?.filter(article =>
    article?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    article?.content?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BookOpen className="w-8 h-8 text-indigo-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Knowledge Base Editor</h1>
                <p className="text-sm text-gray-500 mt-1">Create and manage role-specific documentation</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowTemplatesPanel(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FileText className="w-4 h-4 mr-2" />
                Templates & Guidelines
              </button>
              <button
                onClick={() => {
                  setEditingArticle(null);
                  setShowEditorModal(true);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Article
              </button>
            </div>
          </div>

          {/* Search and Filter */}
          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-5 h-5 text-gray-400" />
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
                <option value="under_review">Under Review</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            {success}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : filteredArticles?.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-500">Create your first article to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {filteredArticles?.map(article => (
              <div
                key={article?.id}
                className="bg-white rounded-lg border border-gray-200 hover:border-indigo-300 transition-colors"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{article?.title}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          article?.status === 'published' ? 'bg-green-100 text-green-800' :
                          article?.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                          article?.status === 'archived'? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {article?.status}
                        </span>
                        {article?.isFeatured && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium">
                            Featured
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{article?.excerpt}</p>
                      <div className="flex items-center space-x-4 text-xs text-gray-500">
                        {article?.category && (
                          <span className="px-2 py-1 bg-gray-100 rounded-full">{article?.category?.name}</span>
                        )}
                        <span className="capitalize">{article?.articleType}</span>
                        <span>{article?.viewCount} views</span>
                        {article?.roles?.length > 0 && (
                          <span>{article?.roles?.length} roles assigned</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => {
                          setEditingArticle(article);
                          setPreviewMode(true);
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingArticle(article);
                          setShowEditorModal(true);
                        }}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteArticle(article?.id)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Templates Panel */}
      {showTemplatesPanel && (
        <TemplatesPanel
          onUseTemplate={handleUseTemplate}
          onClose={() => setShowTemplatesPanel(false)}
        />
      )}

      {/* Editor Modal */}
      {showEditorModal && (
        <EditorModal
          article={editingArticle}
          categories={categories}
          roles={roles}
          onSave={handleSaveArticle}
          onClose={() => {
            setShowEditorModal(false);
            setEditingArticle(null);
          }}
        />
      )}

      {/* Preview Modal */}
      {previewMode && editingArticle && (
        <PreviewModal
          article={editingArticle}
          onClose={() => {
            setPreviewMode(false);
            setEditingArticle(null);
          }}
        />
      )}
    </div>
  );
}

function EditorModal({ article, categories, roles, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    slug: article?.slug || '',
    content: article?.content || '',
    excerpt: article?.excerpt || '',
    articleType: article?.articleType || 'guide',
    status: article?.status || 'draft',
    categoryId: article?.categoryId || '',
    isFeatured: article?.isFeatured || false,
    tags: article?.tags?.join(', ') || '',
    roleIds: article?.roles?.map(r => r?.roleId) || []
  });

  const handleSubmit = (e) => {
    e?.preventDefault();
    onSave({
      ...formData,
      tags: formData?.tags?.split(',')?.map(t => t?.trim())?.filter(t => t)
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {article ? 'Edit Article' : 'Create New Article'}
            </h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-2 gap-6">
            {/* Left Column - Content */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  required
                  value={formData?.title}
                  onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL)</label>
                <input
                  type="text"
                  value={formData?.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e?.target?.value })}
                  placeholder="auto-generated-from-title"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                <textarea
                  rows={2}
                  value={formData?.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                <textarea
                  required
                  rows={12}
                  value={formData?.content}
                  onChange={(e) => setFormData({ ...formData, content: e?.target?.value })}
                  placeholder="Use markdown for formatting..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                />
              </div>
            </div>

            {/* Right Column - Metadata */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData?.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Select category</option>
                  {categories?.map(cat => (
                    <option key={cat?.id} value={cat?.id}>{cat?.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Article Type</label>
                <select
                  value={formData?.articleType}
                  onChange={(e) => setFormData({ ...formData, articleType: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="guide">Guide</option>
                  <option value="tutorial">Tutorial</option>
                  <option value="faq">FAQ</option>
                  <option value="policy">Policy</option>
                  <option value="troubleshooting">Troubleshooting</option>
                  <option value="reference">Reference</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData?.status}
                  onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="under_review">Under Review</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={formData?.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e?.target?.value })}
                  placeholder="e.g., invoices, billing, tutorial"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Visible to Roles (leave empty for all authenticated users)
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {roles?.map(role => (
                    <label key={role?.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData?.roleIds?.includes(role?.id)}
                        onChange={(e) => {
                          if (e?.target?.checked) {
                            setFormData({ ...formData, roleIds: [...formData?.roleIds, role?.id] });
                          } else {
                            setFormData({ ...formData, roleIds: formData?.roleIds?.filter(id => id !== role?.id) });
                          }
                        }}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{role?.displayName}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isFeatured"
                  checked={formData?.isFeatured}
                  onChange={(e) => setFormData({ ...formData, isFeatured: e?.target?.checked })}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isFeatured" className="ml-2 block text-sm text-gray-900">
                  Featured Article
                </label>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Article
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PreviewModal({ article, onClose }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full my-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Preview</h2>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{article?.title}</h1>
            <p className="text-lg text-gray-600">{article?.excerpt}</p>
          </div>

          <div className="prose max-w-none">
            <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
              {article?.content}
            </div>
          </div>

          {article?.tags?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-wrap gap-2">
                {article?.tags?.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                    <TagIcon className="w-3 h-3 mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}